import { useState } from 'react';
import { useParams } from 'react-router';
import { message, TableColumnsType } from 'antd';
import { ApolloError, useMutation, useQuery } from '@apollo/client';

import { useLocationName } from '../../../../hooks/useLocationName';
import { useUserContext } from '../../../../context/PermissionsContext';

import {
  FETCH_PROCESS_AREAS_IT_APP_DASHBOARD,
  SET_PROCESS_AREAS_DEPENDENCY_VALUES,
} from './queries';

import {
  DashboardData,
  FormattedProcessAreaData,
  FormattedProcessAreaItem,
  GetProcessAreaItApplicationsDashboardResponse,
} from './types';

import { tableSortingMethods } from '../../../../utils';

import QueryErrorBoundary from '../../../../components/error-handling';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import DependenciesTitle from '../../../../components/common/DependenciesTitle';
import FinancialData from '../../../../components/common/FinancialData';
import { AntModal, Comment, Footer, TitleBar } from '../../../../components';
import TableV3 from '../../../../components/generic/Table/TableV3';

import { MappingCommentForm, TriStateCheckbox } from '../../components';
import { TriState } from '../../components/TriStateCheckbox';
import { ProcessAreaAndTaggingTitle } from '../../constants';
import { calculateTableColumnWidth } from '../../../../utils/calculateTableColumnWidth';

const formatProcessAreasDataById = (
  data: DashboardData,
): FormattedProcessAreaData => {
  return data?.locations?.map(({ processAreas }) => {
    const { processes: children, applications, ...rest } = processAreas[0];
    const formattedApplications = applications?.map((ITApp) => ({
      ...ITApp,
      comment: ITApp.description ?? '',
    }));
    return {
      ...rest,
      applications: formattedApplications,
      name: processAreas[0].name,
      children: children.map((process) => {
        const { applications, ...rest } = process;
        const formattedApplications = applications?.map((ITApp) => ({
          ...ITApp,
          comment: ITApp.description ?? '',
        }));
        return {
          ...rest,
          applications: formattedApplications,
          children: [],
        };
      }),
    };
  });
};

const PROCESS_AREAS_DEPENDENCY_TYPES = {
  LocationDependency: 'LOCATION',
  ItProcessAreaDependency: 'PROCESS_AREA',
  ItProcessDependency: 'PROCESS',
};

const CHECKBOX_STATES: Record<string, TriState> = {
  CHECKED: 'checked',
  UNCHECKED: 'unchecked',
  PARTIAL: 'line-through',
};

const LocationProcessAreasTagging = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { locationId, processAreaId } = useParams();
  const { locationName } = useLocationName(locationId);
  const { canViewFinancials, canEdit } = useUserContext();

  const { data, loading, error, refetch } =
    useQuery<GetProcessAreaItApplicationsDashboardResponse>(
      FETCH_PROCESS_AREAS_IT_APP_DASHBOARD,
      {
        variables: {
          data: locationId,
          ...(processAreaId && { processAreaUUID: processAreaId }),
        },
      },
    );

  const [
    updateApplicationMapping,
    { loading: updateApplicationMappingLoading },
  ] = useMutation(SET_PROCESS_AREAS_DEPENDENCY_VALUES, {
    onCompleted: refetch,
  });

  const processAreasData = data?.getProcessAreaItApllicationsDashboard;
  const formattedData = processAreasData
    ? formatProcessAreasDataById(processAreasData)
    : [];

  const ITApplications = formattedData[0]?.applications ?? [];

  const loadingStates = loading || updateApplicationMappingLoading;

  const updateITAppMappingDependency = async (
    {
      itApplicationsUuid,
      dependencyUuid,
      dependencyType,
    }: {
      itApplicationsUuid: string;
      dependencyUuid: string;
      dependencyType: string;
    },
    value: boolean,
  ) => {
    try {
      const response = await updateApplicationMapping({
        variables: {
          data: {
            dependencyUuid,
            dependencyType,
            itApplicationValues: [
              {
                itApplicationsUuid,
                selected: value,
              },
            ],
          },
        },
      });

      if (response?.data?.setProcessAreaDependencyPercentage) {
        message.success('Changes Saved');
      }
    } catch (e) {
      if (e instanceof ApolloError) {
        message.error(e.message);
        return;
      }
      message.error('Failed to Save Changes');
    }
  };

  const handleSaveComment = async (uuid: string, updatedComment: string) => {
    const location = formattedData[0];
    const { uuid: locationUuid, __typename } = location;
    try {
      const response = await updateApplicationMapping({
        variables: {
          data: {
            itApplicationValues: [
              {
                description: updatedComment,
                itApplicationsUuid: uuid,
              },
            ],
            dependencyType:
              PROCESS_AREAS_DEPENDENCY_TYPES[
                __typename as keyof typeof PROCESS_AREAS_DEPENDENCY_TYPES
              ],
            dependencyUuid: locationUuid,
          },
        },
      });
      if (response?.data?.setProcessAreaDependencyPercentage)
        message.success('Changes Saved');
    } catch (e) {
      if (e instanceof ApolloError) {
        message.error(e.message);
        return;
      }
      message.error('Failed to Save Changes');
    }
  };

  const applicationsColumns: TableColumnsType<FormattedProcessAreaItem> =
    ITApplications?.map((appGlobal) => ({
      key: appGlobal.uuid,
      title: (
        <DependenciesTitle name={appGlobal.name} comment={appGlobal.comment} />
      ),
      width: calculateTableColumnWidth(appGlobal.name),
      align: 'center',
      showSorterTooltip: false,
      render: (_, record) => {
        const applications = [...record.applications];

        const actualApplication = applications.find(
          (app) => app.uuid === appGlobal.uuid,
        );

        const isParentNode =
          Array.isArray(record.children) && record.children.length > 0;

        const childApplications =
          record.children?.flatMap((child) =>
            child.applications.filter((app) => app.uuid === appGlobal.uuid),
          ) || [];

        const someChildrenSelected = childApplications.some(
          (child) => child.selected,
        );
        const allChildrenSelected =
          childApplications.length > 0 &&
          childApplications.every((child) => child.selected);

        let checkboxValue: TriState;

        if (isParentNode) {
          checkboxValue = allChildrenSelected
            ? CHECKBOX_STATES.CHECKED
            : someChildrenSelected
            ? CHECKBOX_STATES.PARTIAL
            : CHECKBOX_STATES.UNCHECKED;
        } else {
          checkboxValue = actualApplication?.selected
            ? CHECKBOX_STATES.CHECKED
            : CHECKBOX_STATES.UNCHECKED;
        }

        const { uuid: dependencyUuid, __typename } = record ?? {};
        const dependencyType =
          PROCESS_AREAS_DEPENDENCY_TYPES[
            __typename as keyof typeof PROCESS_AREAS_DEPENDENCY_TYPES
          ];

        const data = {
          itApplicationsUuid: appGlobal.uuid,
          dependencyUuid,
          dependencyType,
        };

        const handleCheckboxChange = (
          event: React.ChangeEvent<HTMLInputElement>,
        ) => {
          const newCheckedState = event.target.checked;
          updateITAppMappingDependency(data, newCheckedState);
        };

        return (
          <TriStateCheckbox
            disabled={!canEdit}
            value={checkboxValue}
            onChange={handleCheckboxChange}
          />
        );
      },
    }));

  const columns: TableColumnsType<FormattedProcessAreaItem> = [
    {
      title: '',
      width: 300,
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: (
        <span>
          Revenue <br />
          Supported
          <br />
          Annually
        </span>
      ),
      width: 150,
      dataIndex: 'revenue',
      key: 'revenue',
      fixed: 'left',
      align: 'right',
      render: (text: number) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={text} formatType="decimal" />}
        </span>
      ),
    },
    ...applicationsColumns,
  ];

  return (
    <div>
      <TitleUpdater title={ProcessAreaAndTaggingTitle} />
      <TitleBar
        title={ProcessAreaAndTaggingTitle}
        subtitle={`${locationName} Application Tagging`}
        brief="In the matrix, check the respective process areas or processes supported by each application."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          columns={columns}
          data={formattedData}
          loading={loadingStates}
          customStyles="nested-columns--custom custom-table--dependency-mapping"
          scroll={{ x: 800, y: 540 }}
          tableSize="small"
          extendOptions={
            <div className="w-full flex items-center gap-2 min-w-max">
              <button
                onClick={() => setIsModalOpen(true)}
                className="text-blue underline hover:cursor-pointer font-normal"
                aria-label="Add / Edit Commentary"
              >
                Add / Edit Commentary
              </button>
              <Comment />
            </div>
          }
        />
      </QueryErrorBoundary>
      <AntModal
        title="Add Commentary"
        openModal={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={800}
      >
        <MappingCommentForm<any>
          title={'IT Applications'}
          data={ITApplications}
          onSave={handleSaveComment}
        />
      </AntModal>
      <div className="flex items-center justify-between">
        <Footer
          backPath={`/cyber/location-level/${locationId}/process-areas-and-processes/${processAreaId}/it-application-selection`}
          nextPath={`/cyber/location-level/${locationId}/process-areas-and-processes/${processAreaId}/summary`}
          goNextText="Summary"
        />
      </div>
    </div>
  );
};

export default LocationProcessAreasTagging;
